import {ClockIcon} from '@primer/octicons-react'
import {Button} from '@primer/react'
import type {CompletedReview} from '@github-ui/dotcom-schema/types/pull_requests/v1/review_requests'
import {useRouteParams} from '@github-ui/react-core/future/use-route-params'
import {Link} from '@github-ui/react-router'

import {ForYouItem} from '../shared/ForYouItem'
import {ForYouLeadingIcon} from '../shared/ForYouLeadingIcon'
import {pullRequestsLayoutRoute} from '../../../../routes/layout-route'
import {pullRequestsChangesWithRangeRoute} from '../../../../routes/files-changed-routes'
import {usePullRequestAnalytics} from '../../../../hooks/use-pull-request-analytics'
import {useLayoutRouteData} from '../../../../page-data/loaders/use-layout-route-data'
import {useReviewData} from './use-review-data'

export function shouldShowChangesSinceLastReview(
  review: CompletedReview | null | undefined,
  pullRequestHeadSha: string,
): review is CompletedReview {
  if (!review || review.headSha === pullRequestHeadSha) return false
  return true
}

export function changesSinceLastReviewPath({
  owner,
  repo,
  prNumber,
  headSha,
}: {
  owner: string
  repo: string
  prNumber: string
  headSha: string
}) {
  return pullRequestsChangesWithRangeRoute.generatePath({
    owner,
    repo,
    pr_number: prNumber,
    range: `${headSha}..HEAD`,
  })
}

export function ChangesSinceLastReview({variant}: {variant?: 'default' | 'embedded'}) {
  const {owner, repo, pr_number} = useRouteParams(pullRequestsLayoutRoute)
  const {sendPullRequestAnalyticsEvent} = usePullRequestAnalytics()
  const {pullRequest} = useLayoutRouteData()
  const {data: review, queryStatus} = useReviewData()

  if (queryStatus !== 'success' || !shouldShowChangesSinceLastReview(review, pullRequest.headSha)) return null

  const changesPath = changesSinceLastReviewPath({
    owner,
    repo,
    prNumber: pr_number,
    headSha: review.headSha,
  })

  return (
    <ForYouItem
      leadingVisual={<ForYouLeadingIcon icon={ClockIcon} variant="warning" emphasized />}
      title="New changes"
      itemName="changes-since-last-review"
      variant={variant}
      emphasizedLeadingVisual
      description="Review what changed since your last review."
      actionSlot={
        <Button
          as={Link}
          to={changesPath}
          size="small"
          onClick={() =>
            sendPullRequestAnalyticsEvent('overview.for_you_item_action', 'OVERVIEW_FOR_YOU_ITEM', {
              item_name: 'changes-since-last-review',
              action_name: 'view_new_changes',
            })
          }
        >
          View new changes
        </Button>
      }
    />
  )
}
